import { ReactNode, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { PencilIcon, PlusIcon, Trash2Icon, XIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ConfirmationDialog, Row, Text } from "@/components";

import { SkillsContent } from "@/components/skills/SkillsContent";
import { SkillsView } from "@/components/skills/SkillsView";
import { SkillsWrapper } from "@/components/skills/SkillsWrapper";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useSkill } from "@/hooks/mutations/useSkill";
import { CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME } from "@/lib/constants";
import { SkillType } from "@/types";

const skillSchema = z.object({
  skill: z.string().min(1, { message: "Required" }),
});

const SkillsEditControls = ({
  isEditing,
  onDelete,
  onEdit,
  showDeleteButton,
}: {
  isEditing: boolean; // TODO: Better name for this...
  onDelete: () => void;
  onEdit: () => void;
  showDeleteButton: boolean;
}) => {
  if (!isEditing) {
    return (
      <div>
        <Button onClick={onEdit} size="icon" variant="ghost">
          <PencilIcon className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
        </Button>
        {showDeleteButton && (
          <Button onClick={onDelete} size="icon" variant="ghost">
            <Trash2Icon className="h-4 w-4 text-red-500" />
          </Button>
        )}
      </div>
    );
  }

  return null;
};

const SkillsForm = ({
  form,
  handleCreate,
  handleDelete,
  isMutating,
  skills,
}: {
  form: ReturnType<typeof useForm<z.infer<typeof skillSchema>>>;
  handleCreate: (data: z.infer<typeof skillSchema>) => void;
  handleDelete: (skill: SkillType) => void;
  isMutating: boolean;
  skills: SkillType[];
}) => {
  const onSubmit = (data: z.infer<typeof skillSchema>) => {
    handleCreate(data);
  };

  return (
    <Column className="gap-4">
      <Form {...form}>
        <form
          className="flex flex-col gap-1"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <Text className={CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME}>
            Add Skill
          </Text>
          <Row className="flex-wrap items-start gap-2">
            <FormField
              control={form.control}
              name="skill"
              render={({ field }) => (
                <FormItem className="min-w-44 max-w-[300px] flex-1 text-slate-700">
                  <FormControl>
                    <Input
                      className="h-8 border-slate-300 px-2 text-sm"
                      placeholder="Enter a skill"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              className="h-8 shrink-0 px-3"
              disabled={isMutating}
              type="submit"
              variant="outlineSecondary"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add
            </Button>
          </Row>
        </form>
      </Form>
      <Row className="flex-wrap gap-2">
        {skills.map((skill) => (
          <Button
            className="h-[26px] gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-600"
            key={skill.id}
            onClick={() => {
              handleDelete(skill);
            }}
            variant="ghost"
          >
            {skill.name}
            <XIcon className="h-3 w-3" />
          </Button>
        ))}
      </Row>
    </Column>
  );
};

const SkillsEdit = ({
  skills,
  userId,
}: {
  skills: SkillType[];
  userId: number;
}) => {
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isShowingUpdateForm, setIsShowingUpdateForm] = useState(true);

  const { bulkDeleteSkills, createSkill, deleteSkill } = useSkill();

  const isMutating =
    bulkDeleteSkills.isPending ||
    createSkill.isPending ||
    deleteSkill.isPending;

  const form = useForm<z.infer<typeof skillSchema>>({
    defaultValues: {
      skill: "",
    },
    resolver: zodResolver(skillSchema),
  });

  // useSkill updates the sheet-data cache at the hook level, so success
  // handlers here only manage local form state.
  const handleCreateSkill = (data: z.infer<typeof skillSchema>) => {
    createSkill.mutate(
      {
        userId,
        data: { name: data.skill, userId },
      },
      {
        onSuccess: () => {
          form.reset();
        },
      },
    );
  };

  const handleDeleteSkill = (skill: SkillType) => {
    deleteSkill.mutate({ id: skill.id, userId });
  };

  const handleEdit = () => {
    setIsShowingUpdateForm(true);
  };

  const handleDeleteSkills = () => {
    bulkDeleteSkills.mutate({ userId });
    setIsDeleteConfirmationOpen(false);
  };

  const openDeleteConfirmation = () => {
    setIsDeleteConfirmationOpen(true);
  };

  return (
    <SkillsWrapper
      editControls={
        <SkillsEditControls
          isEditing={isShowingUpdateForm}
          onDelete={openDeleteConfirmation}
          onEdit={handleEdit}
          showDeleteButton={skills.length > 0}
        />
      }
      // If we're showing the update form, we don't want to show the "empty" styles.
      isEmpty={skills.length === 0 && !isShowingUpdateForm}
    >
      <>
        {isShowingUpdateForm ? (
          <SkillsForm
            form={form}
            handleCreate={handleCreateSkill}
            handleDelete={handleDeleteSkill}
            isMutating={isMutating}
            skills={skills}
          />
        ) : (
          <SkillsContent skills={skills} />
        )}
        <ConfirmationDialog
          confirmationButtonVariant="destructive"
          confirmButtonText="Delete"
          description="Are you sure you want to delete ALL skills for this candidate? This action cannot be undone."
          onConfirm={handleDeleteSkills}
          onOpenChange={setIsDeleteConfirmationOpen}
          open={isDeleteConfirmationOpen}
          title="Delete all skills"
        />
      </>
    </SkillsWrapper>
  );
};

export const SheetSkills = ({
  isEditing = false,
  skills,
  titleActions,
  userId,
}: {
  isEditing?: boolean;
  skills: SkillType[];
  titleActions?: ReactNode;
  // Present when the profile is editable.
  userId?: number;
}) => {
  // Filter out skills without names.
  const filteredSkills = skills.filter((skill) => skill.name?.trim());

  if (isEditing && userId !== undefined) {
    return <SkillsEdit skills={filteredSkills} userId={userId} />;
  }

  return <SkillsView skills={filteredSkills} titleActions={titleActions} />;
};
